// @flow
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {Typography} from '@material-ui/core';
import {commaFormat} from '../../../../utils/number';
import {getImageUrl} from '../../../../utils/image_provider';
import {classNames} from '../../../../utils/react';
import {colors} from '../../../../assets/styles/colors';
import type {
  ManualOrderProductOptionGroup,
  SelectedProductOptions,
  ValidationError,
} from '../../types/productOptions';

const useStyles = makeStyles(theme => ({
  optionItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '.25rem',
    border: `1px solid ${colors.grey300}`,
    borderRadius: '.75rem',
    marginBottom: '.75rem',
    backgroundColor: 'white',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: colors.primary,
    },
  },
  optionItemSelected: {
    borderColor: colors.primary,
    backgroundColor: colors.lightPurple,
  },
  optionItemContent: {
    flex: 1,
    marginLeft: theme.spacing(1),
  },
  optionItemName: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: colors.grey800,
  },
  optionItemPrice: {
    fontSize: '0.75rem',
    color: colors.grey600,
    marginTop: '2px',
  },
  optionThumbIndicator: {
    width: '40px',
    height: '40px',
    borderRadius: '.5rem',
    border: `2px solid ${colors.grey300}`,
  },
  optionSwatchImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '.75rem',
  },
}));

export type BaseOptionRendererProps = {
  group: ManualOrderProductOptionGroup,
  selectedProductOptions: SelectedProductOptions,
  currencySymbol: string,
  validationErrors?: ValidationError[],
  onOptionToggle: (groupId: string, optionId: string) => void,
  onTextChange: (groupId: string, optionId: string, value: string) => void,
  onQuantityChange: (
    groupId: string,
    optionId: string,
    quantity: number
  ) => void,
};

type Props = BaseOptionRendererProps;

function ListingOptionRenderer({
  group,
  selectedProductOptions,
  currencySymbol,
  onOptionToggle,
}: Props) {
  const classes = useStyles();
  const groupId = group.id;

  return (
    <>
      {group.options.map(option => {
        if (!option.enabled) return null;
        const optionId = option.id;
        const selectedOption = selectedProductOptions[groupId]?.[optionId];
        const isSelected = selectedOption?.selected || false;

        const hasImage = !!option.storeProductOption.imageId;
        const imageSrc = getImageUrl(option.storeProductOption.imageId);

        const optionColor = option.storeProductOption.color;

        const displayThumb = hasImage || optionColor;

        return (
          <div
            key={optionId}
            className={classNames(classes.optionItem, {
              [classes.optionItemSelected]: isSelected,
            })}
            onClick={() => onOptionToggle(groupId, optionId)}
          >
            {displayThumb && (
              <div
                className={classes.optionThumbIndicator}
                style={{
                  backgroundColor: optionColor,
                }}
              >
                {hasImage && (
                  <img
                    src={imageSrc}
                    alt=""
                    className={classes.optionSwatchImage}
                  />
                )}
              </div>
            )}
            <div className={classes.optionItemContent}>
              <Typography className={classes.optionItemName}>
                {option.storeProductOption.name}
              </Typography>
              <Typography className={classes.optionItemPrice}>
                +{commaFormat(parseFloat(option.price || 0), currencySymbol)}
              </Typography>
            </div>
          </div>
        );
      })}
    </>
  );
}

export default ListingOptionRenderer;
